import React, { useEffect, useState } from 'react';
import { submitAndroidTester } from '../services/submitAndroidTester';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AndroidTesters: React.FC = () => {
  const [email, setEmail] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle');

  useEffect(() => {
    document.title = 'Pixano — Android Test';
  }, []);

  const emailValid = EMAIL_REGEX.test(email.trim());
  const canSubmit = emailValid && accepted && status !== 'submitting';

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setStatus('submitting');
    try {
      await submitAndroidTester(email.trim().toLowerCase(), accepted);
      setStatus('done');
    } catch (err) {
      console.error('Android tester signup failed:', err);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F14] text-white flex flex-col items-center px-5 py-10">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-10">
          <img src="/pix-logo.png" alt="Pixano" className="h-16 w-auto" />
        </div>

        <div className="rounded-xl border border-[rgba(0,227,255,0.14)] bg-[linear-gradient(180deg,rgba(0,227,255,0.055)_0%,transparent_52%),#0A0E13] p-6 sm:p-8">
          {status === 'done' ? (
            <div className="text-center py-4">
              <h1 className="text-xl font-semibold mb-3">You're on the Android tester list.</h1>
              <p className="text-white/80 text-sm leading-relaxed">
                Watch for the Google Play testing invite and check Discord for testing updates.
              </p>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-semibold mb-2">Join the Pixano Android Test</h1>
              <p className="text-white/70 text-sm leading-relaxed mb-6">
                Enter the email address connected to your Google Play account so you can be added to the Android tester list.
              </p>

              <label htmlFor="gp-email" className="block text-sm text-white/80 mb-2">
                Email address used for Google Play
              </label>
              <input
                id="gp-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg bg-[#0B0F14] border border-white/15 px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#00e3ff] focus:shadow-[0_0_10px_rgba(0,227,255,0.25)] transition-all"
              />

              <label className="flex items-start gap-3 mt-5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-[#00e3ff]"
                />
                <span className="text-sm text-white/80 leading-relaxed">
                  I understand this is an early Android test build and may have bugs.
                </span>
              </label>

              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="mt-6 w-full inline-flex items-center justify-center rounded-lg border border-[#00e3ff] bg-transparent px-6 py-3 text-sm font-medium text-white transition-all hover:bg-[rgba(0,227,255,0.1)] hover:shadow-[0_0_14px_rgba(0,227,255,0.28)] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:shadow-none"
              >
                {status === 'submitting' ? 'Joining…' : 'Join Android Test'}
              </button>

              {status === 'error' && (
                <p className="text-sm text-red-400 mt-4 text-center">
                  Something went wrong. Please check your connection and try again.
                </p>
              )}

              <p className="text-xs text-white/45 leading-relaxed mt-6">
                Use the same email you use on the Google Play Store. If the email is different, you may not be able to access the test build.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AndroidTesters;
